import { routes } from '@metaclaude/shared';
/**
 * The getting-set-up checklist, as a pure function.
 *
 * A fresh deployment has half a dozen one-time steps spread across four
 * screens, and each was historically discovered by hitting the wall it
 * guards: no credential — runs fail opaquely; no updater — Apply never
 * appears; no notifications — approvals block silently. The checklist
 * names them in the order they unblock each other, and the card renders
 * whatever this answers so the rule stays readable and testable here.
 */

export interface OnboardingInput {
  /** A Claude credential is live (paired, env, or CLI sign-in). */
  authenticated: boolean;
  workspaces: number;
  /** Any run has ever been recorded. */
  hasRuns: boolean;
  totpEnabled: boolean;
  pushDevices: number;
  /** The host updater answered as installed. */
  updaterAvailable: boolean;
}

export interface OnboardingStep {
  key: string;
  label: string;
  detail: string;
  done: boolean;
  href: string;
}

export function onboardingSteps(input: OnboardingInput): OnboardingStep[] {
  return [
    {
      key: 'pair',
      label: 'Pair Claude',
      detail: 'Sign in with your Pro or Max account — nothing runs without it.',
      done: input.authenticated,
      // The credential card lives under Connections, with the other things
      // this deployment authenticates against — it is not a preference, and it
      // is not the machine either. A step that lands on the wrong screen is
      // worse than no step: the operator arrives, sees nothing to do, and
      // stops trusting the list. It has landed on the wrong one twice now,
      // which is why `onboarding.test.ts` pins each destination.
      href: routes.settingsSection('connections'),
    },
    {
      key: 'workspace',
      label: 'Create a workspace',
      detail: 'A directory plus the agent policy that applies inside it.',
      done: input.workspaces > 0,
      href: routes.workspaces(),
    },
    {
      key: 'run',
      label: 'Run the agent once',
      detail: 'Open a session and ask for something small; watch it work.',
      done: input.hasRuns,
      href: routes.workspaces(),
    },
    {
      key: 'totp',
      label: 'Turn on two-factor auth',
      detail: 'This server is on the network; your account should need more than a password.',
      done: input.totpEnabled,
      href: routes.settingsSection('security'),
    },
    {
      key: 'push',
      label: 'Enable notifications',
      detail: 'A push when a run waits on your approval — the phone is the point.',
      done: input.pushDevices > 0,
      href: routes.server(),
    },
    {
      key: 'updater',
      label: 'Install the host updater',
      detail: 'Re-run deploy/install-app.sh once; updates become one button here.',
      done: input.updaterAvailable,
      href: routes.server(),
    },
  ];
}

export function onboardingDone(steps: OnboardingStep[]): boolean {
  return steps.every((step) => step.done);
}
